import React, { useMemo } from "react"

import { type SerializedStyles, css } from "@emotion/react"

import type { IPasswordValidationRule } from "@front-shared/types/objects.types"

import { NIRVANA_COLORS } from "../config/theme.config"
import { FRONT_PASSWORD_VALIDATION_CONFIG } from "../config/validation.config"
import { PrimaryText } from "./primaryText.element"

import triangleIcon from "@front-shared/assets/images/elements/passwordStrength/triangle.svg"
import checkIcon from "@front-shared/assets/images/icons/check-dark2.svg"

export const PasswordStrengthElement: React.FunctionComponent<
    IPasswordStrengthElementProps
> = props => {
    const passwordRules = useMemo<IPasswordValidationRule[]>(
        () => props.rules || FRONT_PASSWORD_VALIDATION_CONFIG.rules,
        [props.rules],
    )

    const getTrianglePlacementStyles = (): SerializedStyles => {
        switch (props.trianglePlacement) {
            case "topLeft":
                return styles.topLeftTriangle
            case "topRight":
                return styles.topRightTriangle
            case "bottomRight":
                return styles.bottomRightTriangle
            case "bottomLeft":
            default:
                return styles.bottomLeftTriangle
        }
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <div
                css={[
                    styles.innerWrapper,
                    !props.bgClassName
                        ? styles.innerWrapperBackground
                        : undefined,
                ]}
                className={props.bgClassName}
                style={
                    props.width
                        ? {
                              width: `${props.width}px`,
                          }
                        : undefined
                }
            >
                <PrimaryText typography="caption">
                    Your password should have:
                </PrimaryText>

                {passwordRules.map((rule, index) => {
                    const currentValueMatches = !!(
                        props.currentValue || ""
                    ).match(rule.regex)

                    return (
                        <div key={index} css={styles.ruleWrapper}>
                            <PrimaryText typography="h6" css={styles.ruleTitle}>
                                {rule.title}
                            </PrimaryText>

                            {currentValueMatches ? (
                                <img src={checkIcon} alt="Valid rule" />
                            ) : null}
                        </div>
                    )
                })}
            </div>

            {/*TRIANGLE*/}
            {typeof props.trianglePlacement !== "undefined" ? (
                <div
                    css={[styles.triangleWrapper, getTrianglePlacementStyles()]}
                >
                    <img src={triangleIcon} alt="Triangle" />
                </div>
            ) : null}
        </div>
    )
}

const styles = {
    innerWrapper: css({
        paddingTop: 16,
        paddingRight: 24,
        paddingBottom: 16,
        paddingLeft: 24,
        borderRadius: 16,
    }),
    innerWrapperBackground: css({
        backgroundColor: NIRVANA_COLORS.brand.warmLight,
    }),
    ruleWrapper: css({
        marginTop: 16,
        display: "flex",
        alignItems: "center",
    }),
    ruleTitle: css({
        flex: 1,
        marginRight: 16,
    }),
    triangleWrapper: css({
        position: "absolute",
        zIndex: 10,
    }),
    topLeftTriangle: css({
        left: -11,
        top: 16,
    }),
    topRightTriangle: css({
        right: -11,
        top: 16,
        transform: "rotate(180deg)",
    }),
    bottomRightTriangle: css({
        right: -11,
        bottom: 16,
        transform: "rotate(180deg)",
    }),
    bottomLeftTriangle: css({
        left: -11,
        bottom: 16,
    }),
}

interface IPasswordStrengthElementProps {
    className?: string
    width?: number
    bgClassName?: string
    currentValue?: string
    rules?: IPasswordValidationRule[]
    trianglePlacement?: "topLeft" | "topRight" | "bottomRight" | "bottomLeft"
}
